import { AutocompleteItem, Tab, Tabs } from "@heroui/react";
import { t } from "@lingui/core/macro";
import { useDebouncedValue } from "@mantine/hooks";
import { type ReactNode, useState } from "react";
import { LimeAutocomplete } from "@/Components/NextBase/LimeAutocomplete";
import { LimeInput } from "@/Components/NextBase/LimeInput";
import { LimeSwitch } from "@/Components/NextBase/LimeSwitch";
import { api } from "@/lib/api-client";
import type { CompanyShareType } from "@/utils/subscription-shares";

type Props = {
  isEnabled: boolean;
  onEnabledChange: (isEnabled: boolean) => void;
  payerCompanyId: number | null;
  onPayerCompanyIdChange: (payerCompanyId: number | null) => void;
  shareType: CompanyShareType;
  onShareTypeChange: (shareType: CompanyShareType) => void;
  /** Percent when the type is percentage, major currency units when it is fixed. */
  shareValue: number;
  onShareValueChange: (shareValue: number) => void;
  currencySymbol: string;
  /** Hides the estimate wording and warning when nothing will ever be issued. */
  issuesEstimates: boolean;
  /** Whether the company is sent its own estimate on the member's schedule. */
  companyReceivesEstimates: boolean;
  onCompanyReceivesEstimatesChange: (companyReceivesEstimates: boolean) => void;
  /** The member is left no share at all, so none of the copy may promise them an estimate. */
  companyFundsEverything: boolean;
  companyError?: ReactNode;
  valueError?: ReactNode;
  /** Locks who pays and how much; the estimate preference stays editable. */
  isDisabled?: boolean;
};

function getEstimateRecipientsLabel({
  companyFundsEverything,
  companyReceivesEstimates,
}: {
  companyFundsEverything: boolean;
  companyReceivesEstimates: boolean;
}): string {
  if (companyFundsEverything) {
    return companyReceivesEstimates
      ? t`Paket v celoti krije podjetje, zato predračun prejme samo ono.`
      : t`Paket v celoti krije podjetje; predračun mu izdate ročno.`;
  }
  return companyReceivesEstimates
    ? t`Stranka in podjetje prejmeta vsak svoj predračun za ta paket.`
    : t`Stranka prejme predračun za svoj del, podjetju pa ga izdate ročno.`;
}

export function CompanySplitFields({
  isEnabled,
  onEnabledChange,
  payerCompanyId,
  onPayerCompanyIdChange,
  shareType,
  onShareTypeChange,
  shareValue,
  onShareValueChange,
  currencySymbol,
  issuesEstimates,
  companyReceivesEstimates,
  onCompanyReceivesEstimatesChange,
  companyFundsEverything,
  companyError,
  valueError,
  isDisabled = false,
}: Props) {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery.trim(), 300);

  const { data: companies } = api.company.useGetCompanies({
    searchQuery:
      debouncedSearchQuery.length > 0 ? debouncedSearchQuery : undefined,
    pagination: { limit: 20 },
    enabled: isEnabled,
  });

  const companyOnPage = companies?.companies?.find(
    (company) => company.companyId === payerCompanyId,
  );

  // The saved payer may not be on the page of results, and missing it would drop its missing-email warning.
  const { data: payerCompanyPage } = api.company.useGetCompanies({
    companyId: payerCompanyId ?? undefined,
    enabled:
      isEnabled &&
      payerCompanyId != null &&
      companies != null &&
      companyOnPage == null,
  });

  const selectedCompany =
    companyOnPage ??
    payerCompanyPage?.companies?.find(
      (company) => company.companyId === payerCompanyId,
    );

  const isPercentage = shareType === "percentage";

  return (
    <>
      <LimeSwitch
        data-identifier="subscription-company-split-enabled"
        size="sm"
        isCompact
        isSelected={isEnabled}
        onValueChange={onEnabledChange}
        isDisabled={isDisabled}
        title={t`Del paketa plača podjetje`}
      />

      {isEnabled && (
        <div className="flex flex-col gap-4">
          <LimeAutocomplete
            data-identifier="subscription-payer-company"
            label={t`Podjetje`}
            labelPlacement="outside"
            variant="bordered"
            isRequired
            isDisabled={isDisabled}
            error={companyError}
            allowsCustomValue={false}
            inputValue={searchQuery || (selectedCompany?.companyName ?? "")}
            onInputChange={setSearchQuery}
            selectedKey={payerCompanyId != null ? String(payerCompanyId) : null}
            onSelectionChange={(key) => {
              onPayerCompanyIdChange(key == null ? null : Number(key));
            }}
            isVirtualized={false}
          >
            {(companies?.companies ?? []).map((company) => (
              <AutocompleteItem
                key={String(company.companyId)}
                textValue={company.companyName}
              >
                {company.companyName}
              </AutocompleteItem>
            ))}
          </LimeAutocomplete>

          <div className="flex justify-center">
            <Tabs
              radius="full"
              selectedKey={shareType}
              isDisabled={isDisabled}
              onSelectionChange={(type) =>
                onShareTypeChange(type as CompanyShareType)
              }
              disableAnimation
            >
              <Tab key="fixed" title={currencySymbol} />
              <Tab key="percentage" title="%" />
            </Tabs>
          </div>

          <LimeInput
            data-identifier="subscription-company-share-value"
            label={
              isPercentage
                ? t`Delež, ki ga plača podjetje`
                : t`Znesek, ki ga podjetje plačuje`
            }
            labelPlacement="outside"
            placeholder="0"
            type="number"
            isRequired
            isDisabled={isDisabled}
            error={valueError}
            min={isPercentage ? 1 : 0.01}
            max={isPercentage ? 100 : undefined}
            step={isPercentage ? 1 : 0.01}
            endContent={isPercentage ? "%" : currencySymbol}
            value={String(shareValue)}
            onValueChange={(value) => onShareValueChange(Number(value))}
          />

          {issuesEstimates && (
            <>
              <LimeSwitch
                data-identifier="subscription-company-receives-estimates"
                size="sm"
                isCompact
                isSelected={companyReceivesEstimates}
                onValueChange={onCompanyReceivesEstimatesChange}
                title={t`Podjetju se avtomatsko izdajajo predračuni za ta paket.`}
                description={t`Izdan in poslan pred začetkom vsakega obdobja tega paketa.`}
              />

              {payerCompanyId != null && (
                <p className="text-default-500 text-tiny">
                  {getEstimateRecipientsLabel({
                    companyFundsEverything,
                    companyReceivesEstimates,
                  })}
                </p>
              )}

              {selectedCompany != null && !selectedCompany.email && (
                <p className="text-sm text-orange-600">
                  {companyReceivesEstimates
                    ? t`Podjetje nima e-poštnega naslova, zato predračuna ne bo prejelo po e-pošti.`
                    : t`Podjetje nima e-poštnega naslova, zato predračuna ne bo moglo prejeti po e-pošti.`}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}
